import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  Activity,
  SearchCode,
  Bell,
  ClipboardList,
  Settings,
  ShieldCheck,
  X,
  FileSearch,
  CalendarRange,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { SIDEBAR_ITEMS, ROLE_LABELS } from "../../data/seedData";

const ICON_MAP = {
  LayoutDashboard,
  FolderKanban,
  Users,
  Activity,
  SearchCode,
  Bell,
  ClipboardList,
  Settings,
  FileSearch,
  CalendarRange,
};

// Warna ikon tiap menu (cycling) saat inactive
const ITEM_COLORS = [
  "bg-blue-100 text-blue-600",
  "bg-violet-100 text-violet-600",
  "bg-emerald-100 text-emerald-600",
  "bg-amber-100 text-amber-600",
  "bg-rose-100 text-rose-600",
  "bg-sky-100 text-sky-600",
  "bg-indigo-100 text-indigo-600",
  "bg-orange-100 text-orange-600",
];

export default function Sidebar({ mobileOpen, onCloseMobile }) {
  const { user } = useAuth();

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={onCloseMobile}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 z-50 h-screen w-64 flex-col border-r border-blue-100/50 transition-transform duration-200 md:flex md:translate-x-0 ${
          mobileOpen ? "translate-x-0 flex" : "-translate-x-full hidden md:flex"
        }`}
        style={{ background: "linear-gradient(180deg, #eef2ff 0%, #eff6ff 45%, #f8faff 100%)" }}
      >
        {/* Brand header — navy gradient */}
        <div
          className="flex h-16 items-center justify-between px-5 border-b border-blue-900/20"
          style={{ background: "linear-gradient(90deg, #1e3a5f 0%, #2d5898 100%)" }}
        >
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/20">
              <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-[13px] font-bold tracking-wide text-white">
                IRBANSUS
              </div>
              <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-blue-200">
                Kab. Tasikmalaya
              </div>
            </div>
          </div>
          <button
            type="button"
            className="md:hidden rounded-lg p-1.5 text-white/70 hover:bg-white/20 transition-colors"
            onClick={onCloseMobile}
            data-testid="sidebar-close-btn"
            aria-label="Tutup menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4">
          <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Menu Utama
          </div>
          <ul className="space-y-0.5">
            {SIDEBAR_ITEMS.map((item, idx) => {
              const Icon = ICON_MAP[item.icon] || LayoutDashboard;
              const inactiveColor = ITEM_COLORS[idx % ITEM_COLORS.length];
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={onCloseMobile}
                    data-testid={`sidebar-link-${item.path.replace("/", "")}`}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-xl px-2.5 py-2 text-[13px] font-medium transition-all duration-150 ${
                        isActive
                          ? "text-white shadow-md"
                          : "text-slate-600 hover:bg-white/60 hover:text-slate-800"
                      }`
                    }
                    style={({ isActive }) =>
                      isActive
                        ? {
                            background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
                            boxShadow: "0 4px 14px rgba(37,99,235,0.28)",
                          }
                        : {}
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0 transition-all ${
                            isActive
                              ? "bg-white/20 text-white"
                              : `${inactiveColor} group-hover:scale-105`
                          }`}
                        >
                          <Icon className="h-4 w-4" strokeWidth={2} />
                        </div>
                        <span className="truncate">{item.label}</span>
                        {isActive && (
                          <div className="ml-auto h-1.5 w-1.5 rounded-full bg-white/80 flex-shrink-0" />
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User profile at bottom */}
        <div className="border-t border-blue-100/50 p-3">
          <div className="flex items-center gap-3 rounded-xl bg-white/70 px-3 py-2.5 shadow-sm backdrop-blur-sm">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full text-white text-[12px] font-bold flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)" }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-slate-800 truncate">
                {user?.name || "—"}
              </div>
              <div className="text-[10px] text-slate-400 truncate">
                {ROLE_LABELS?.[user?.role] || user?.role || "—"}
              </div>
            </div>
          </div>
          <div className="mt-2 px-2 text-[10px] text-slate-400 text-center">
            © {new Date().getFullYear()} Inspektorat Daerah
          </div>
        </div>
      </aside>
    </>
  );
}
